import { getString, fetchAndActivate } from 'firebase/remote-config';
import { remoteConfig } from './firebase';
import Intercom from '@intercom/messenger-js-sdk';

let intercomInitialized = false;

/**
 * Initialize Intercom chat widget
 * Works for both authenticated and anonymous users
 */
export const initializeIntercom = async (user?: { email?: string; userId?: string; name?: string }): Promise<void> => {
  if (intercomInitialized) {
    console.log('[Intercom] Already initialized');
    // If already initialized but user info provided, update it
    if (user) {
      updateIntercomUser(user);
    }
    return;
  }

  try {
    // Ensure Remote Config is fetched and activated
    console.log('[Intercom] Fetching Remote Config...');
    await fetchAndActivate(remoteConfig);
    
    // Get Intercom configuration from Remote Config
    const appId = getString(remoteConfig, 'intercom_app_id');

    if (!appId) {
      console.warn('[Intercom] App ID not found in Remote Config');
      console.warn('[Intercom] Please add "intercom_app_id" to Firebase Remote Config');
      return;
    }

    console.log('[Intercom] App ID found:', appId);

    // Initialize Intercom settings
    // For anonymous users, only app_id is required
    // For authenticated users, include email, user_id, and name
    const intercomSettings: any = {
      app_id: appId,
    };

    // Add user information if provided
    if (user?.email) {
      intercomSettings.email = user.email;
    }
    if (user?.userId) {
      intercomSettings.user_id = user.userId;
    }
    if (user?.name) {
      intercomSettings.name = user.name;
    }
    
    console.log('[Intercom] Initializing with settings:', {
      app_id: appId,
      has_email: !!user?.email,
      has_userId: !!user?.userId,
      has_name: !!user?.name,
    });

    // Initialize Intercom using the npm package
    Intercom(intercomSettings);
    
    console.log('[Intercom] ✅ Widget initialized successfully');
    intercomInitialized = true;

    // Listen for Intercom events
    if ((window as any).Intercom) {
      (window as any).Intercom('onShow', () => {
        console.log('[Intercom] Widget shown');
      });
      
      (window as any).Intercom('onHide', () => {
        console.log('[Intercom] Widget hidden');
      });
    }
  } catch (error) {
    console.error('[Intercom] ❌ Failed to initialize:', error);
  }
};

/**
 * Update Intercom with user information
 * Can be called when user logs in to associate their identity
 */
export const updateIntercomUser = (user: { email?: string; userId?: string; name?: string }): void => {
  if (!intercomInitialized) {
    console.warn('[Intercom] Not initialized, initializing with user data...');
    initializeIntercom(user);
    return;
  }

  try {
    if ((window as any).Intercom) {
      const updateData: any = {};
      
      if (user.email) {
        updateData.email = user.email;
      }
      if (user.userId) {
        updateData.user_id = user.userId;
      }
      if (user.name) {
        updateData.name = user.name;
      }
      
      (window as any).Intercom('update', updateData);
      console.log('[Intercom] ✅ User updated');
    }
  } catch (error) {
    console.error('[Intercom] ❌ Error updating user:', error);
  }
};

/**
 * Show Intercom messenger
 */
export const showIntercom = (): void => {
  try {
    if ((window as any).Intercom) {
      (window as any).Intercom('show');
    }
  } catch (error) {
    console.error('[Intercom] ❌ Error showing messenger:', error);
  }
};

/**
 * Hide Intercom messenger
 */
export const hideIntercom = (): void => {
  try {
    if ((window as any).Intercom) {
      (window as any).Intercom('hide');
    }
  } catch (error) {
    console.error('[Intercom] ❌ Error hiding messenger:', error);
  }
};

/**
 * Shutdown Intercom (use sparingly - typically only needed for complete cleanup)
 * For logout, prefer resetToAnonymous() to keep the widget available
 */
export const shutdownIntercom = (): void => {
  try {
    if ((window as any).Intercom) {
      (window as any).Intercom('shutdown');
      console.log('[Intercom] ✅ Shutdown complete');
    }
  } catch (error) {
    console.error('[Intercom] ❌ Error shutting down:', error);
  }
};

/**
 * Reset Intercom to anonymous mode (for logout)
 * This keeps the widget available but removes user identification
 */
export const resetToAnonymous = (): void => {
  try {
    if ((window as any).Intercom && (window as any).intercomSettings?.app_id) {
      // Shutdown current session
      (window as any).Intercom('shutdown');
      
      // Reboot in anonymous mode
      (window as any).Intercom('boot', {
        app_id: (window as any).intercomSettings.app_id,
      });
      
      console.log('[Intercom] ✅ Reset to anonymous mode');
    }
  } catch (error) {
    console.error('[Intercom] ❌ Error resetting to anonymous:', error);
  }
};
